import { Stack, Button, CircularProgress, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { ProjectGridField } from '@/types/fields'
import { ProjectGridFieldRule } from '@/types/rules'
import { DraggableList } from '@/components/lists/DraggableList'
import useOverlay from '@/hooks/useOverlay'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { useProjectGridsContext } from '@/components/project-settings/ProjectGridsProvider'
import {
  useCreateProjectGridFieldRule,
  useDeleteProjectGridFieldRule,
  useGetProjectGridFieldRules,
  useUpdateProjectGridFieldRules,
} from '@/service-library/hooks/project-grid-field-rules'
import { getBaseGrid } from '@/utils/field'
import queryKeys from '@/service-library/query-keys'
import RuleCard from '@/components/rules/RuleCard'
import AddFieldRuleDialog from './AddFieldRuleDialog'

type FieldRulesProps = {
  field: ProjectGridField
  fieldRulesQuery: ReturnType<typeof useGetProjectGridFieldRules>
  selectedRuleId: string | undefined
  setSelectedRuleId: (ruleId: string | undefined) => void
}

export default function FieldRules({
  field,
  fieldRulesQuery,
  selectedRuleId,
  setSelectedRuleId,
}: FieldRulesProps) {
  const { projectGridFieldRules = [], isPending, queryKey } = fieldRulesQuery

  const { createProjectGridFieldRule } = useCreateProjectGridFieldRule({
    sideEffectQueryKeys: [queryKeys.projectGridFields.lists()],
    listQueryKey: queryKey,
    onMutate: (newFieldRule) => {
      setSelectedRuleId(newFieldRule.id)
    },
    onSuccess: () => {
      showSuccessSnackbar('Field Rule Created')
    },
    onError: () => {
      showErrorSnackbar('Failed to create field rule. Please try again later.')
      setSelectedRuleId(undefined)
    },
  })

  const { deleteProjectGridFieldRule } = useDeleteProjectGridFieldRule({
    sideEffectQueryKeys: [queryKeys.projectGridFields.lists()],
    listQueryKey: queryKey,
    onMutate: () => {
      setSelectedRuleId(undefined)
    },
    onError: () => {
      showErrorSnackbar('Failed to delete field rule. Please try again later.')
    },
  })

  const { updateProjectGridFieldRules } = useUpdateProjectGridFieldRules({
    sideEffectQueryKeys: [queryKeys.projectGridFields.lists()],
    listQueryKey: queryKey,
    useSortOrder: true,
    onMutate: (updatedFieldRules) => {
      if (selectedRuleId) {
        const updatedSelectedRule = updatedFieldRules.find(
          ({ id }) => selectedRuleId === id,
        )
        if (updatedSelectedRule) {
          setSelectedRuleId(updatedSelectedRule.id)
        }
      }
    },
  })

  const reorderFieldRules = (reorderedFieldRules: ProjectGridFieldRule[]) => {
    const sortedFieldRules = reorderedFieldRules.map((item, index) => ({
      ...item,
      sort_order: index,
    }))
    updateProjectGridFieldRules(sortedFieldRules)
  }

  const { projectGrids = [] } = useProjectGridsContext()
  const baseGrid = getBaseGrid(projectGrids)

  const addRuleOverlay = useOverlay()

  return (
    <Stack spacing={4}>
      {isPending && <CircularProgress size={40} />}

      {!isPending && (
        <>
          {/* MARK: Rules */}
          <Stack spacing={1}>
            {projectGridFieldRules.length > 0 && (
              <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                <DraggableList
                  data={projectGridFieldRules}
                  onChange={reorderFieldRules}
                >
                  {projectGridFieldRules.map((fieldRule) => (
                    <RuleCard
                      key={fieldRule.id}
                      onDelete={() => deleteProjectGridFieldRule(fieldRule.id)}
                      isSelected={selectedRuleId === fieldRule.id}
                      onClick={() => {
                        setSelectedRuleId(fieldRule.id)
                      }}
                      name={fieldRule.rule.name}
                      description={fieldRule.rule.description || ''}
                      funcName={fieldRule.params.function_name}
                      enabled={fieldRule.is_enabled}
                      onEnableDisable={(checked) => {
                        updateProjectGridFieldRules([
                          {
                            ...fieldRule,
                            is_enabled: checked,
                          },
                        ])
                      }}
                    />
                  ))}
                </DraggableList>
              </Box>
            )}

            <div>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={addRuleOverlay.open}
              >
                Add Rule
              </Button>
              <AddFieldRuleDialog
                overlay={addRuleOverlay}
                fieldId={field.id}
                isTableField={baseGrid?.id !== field.project_grid_id}
                onSubmit={createProjectGridFieldRule}
              />
            </div>
          </Stack>
        </>
      )}
    </Stack>
  )
}
